import { useFormik } from 'formik'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import * as Yup from 'yup'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import { Container } from '../../../assets/styles/GlobalStyle'
import Loader from '../../../components/Loader'
import PageHeader from '../../../components/PageHeader'
import BirthDayInput from '../../../components/v2/BirthDayInput'
import Checkbox from '../../../components/v2/buttons/Checkbox'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import Dropdown from '../../../components/v2/Dropdown'
import GenderCard from '../../../components/v2/GenderCard'
import Input from '../../../components/v2/Input'
import DialogModal from '../../../components/v2/modals/DialogModal'
import PhoneNumberInput from '../../../components/v2/PhoneNumberInput'
import Title from '../../../components/v2/Title'
import UploadFile from '../../../components/v2/UploadFile'
import WarningMessage from '../../../components/v2/WarningMessage'
import { InternationalTplCountry, InternationalTplUser } from '../../../domain/types'
import { RootState, useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import { AuthState } from '../../../store/authSlice'
import { IntTplState, setUserField, setUserTranslationField } from '../../../store/internationalTplSlice'
import { useInternationalTplPages } from './useInternationalTplPages'
import { useInternationalTplProgress } from './useInternationalTplProgress'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

const InternationalTplPersonalInfo = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  const auth = useSelector<RootState, AuthState>((state) => state.auth)
  const { checkout, filter } = useAppSelector((data: { internationalTpl: IntTplState }) => data.internationalTpl)
  const { data: pageInfo, isLoading: isPageInfoLoading } = api.useGetIntTplPageInfoQuery(
    'international-tpl-personal-info',
  )

  const [termsModalIsOpen, setTermsModalIsOpen] = useState<boolean>(false)

  const { data: internationalTplCountries, isLoading: isInternationalTplCountriesLoading } =
    api.useGetInternationalTplCountriesQuery(undefined)

  const [getClientTypes, { data: client, isLoading: isClientLoading }] = api.useGetIntTplClientTypeMutation()

  // Use the hook to get pages and page data from Redux
  const { pages: pagesFromHook, isLoading: isPagesLoading } = useInternationalTplPages()
  const { totalCount, passedCount, pages: progressPages } = useInternationalTplProgress()
  const displayPages = progressPages.length > 0 ? progressPages : pagesFromHook

  const isUserIdentificated = auth?.user?.identificated === true
  const personalFieldsDisabled = isUserIdentificated
  const genderOptions = useMemo(() => ['female', 'male'] as const, [])

  // Helper function to safely get ISO code from cityzenship (handles both string ISO code and number country ID)
  const getCityzenshipIso = useCallback(
    (cityzenship: any): string | undefined => {
      if (!cityzenship) return undefined
      if (typeof cityzenship === 'string') return cityzenship
      if (typeof cityzenship === 'number' && internationalTplCountries) {
        const country = internationalTplCountries.find((c) => c.id === cityzenship)
        return country?.iso
      }
      return undefined
    },
    [internationalTplCountries],
  )

  useEffect(() => {
    if (filter && filter.isoCode && filter.day) {
      const _getDaysRequest = async () => {
        try {
          await getClientTypes({ countryCode: filter.isoCode, insuranceDays: filter.day as number }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (_getDaysRequest) (single)')
          }
        }
      }
      // call request
      _getDaysRequest()
    }
    return () => {}
  }, [filter, getClientTypes])

  useEffect(() => {
    if (internationalTplCountries && checkout.user?.cityzenship) {
      const iso = internationalTplCountries.find((x) => x.id === checkout.user?.countryId)?.iso

      setFieldValue('cityzenship', iso || checkout.user.cityzenship)
      dispatch(
        setUserField({
          fieldName: 'cityzenship',
          fieldValue: iso || checkout.user.cityzenship,
        }),
      )
    }
  }, [checkout.user?.cityzenship])

  const ValidationSchema = useMemo(() => {
    // english letter regex
    const englishLetterRegex = /^[A-Za-z]+$/
    // georgian letter regex
    const georgianLetterRegex = /^[\u10D0-\u10FF]*$/

    return Yup.object({
      cityzenship: Yup.string().when([], {
        is: () => checkout.user.clientType === 'individual',
        then: (schema) => schema.required(`${t('required')}`),
        otherwise: (schema) => schema.notRequired(),
      }),
      idNumber: Yup.string()
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        .when([], {
          is: () => checkout.user.clientType === 'individual',
          then: (schema) =>
            schema.when('cityzenship', {
              is: (val: any) => {
                const isoCode = getCityzenshipIso(val)
                return isoCode === 'GE'
              },
              then: (s) =>
                s
                  .min(11, `${t('personalNumberValidation')}`)
                  .max(11, `${t('personalNumberValidation')}`)
                  .required(`${t('required')}`),
              otherwise: (s) => s.required(`${t('required')}`),
            }),
          otherwise: (schema) => schema.notRequired(),
        }),
      socialSecurityNumber: Yup.string()
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        .when([], {
          is: () => {
            const isoCode = getCityzenshipIso(checkout.user?.cityzenship)
            return isoCode?.toLowerCase() === 'hy' && checkout.user?.clientType === 'individual'
          },
          then: (schema) => schema.required(`${t('required')}`),
          otherwise: (schema) => schema.notRequired(),
        }),
      dateOfbirth: Yup.string().when([], {
        is: () => checkout.user.clientType === 'individual',
        then: (schema) => schema.required(`${t('required')}`),
        otherwise: (schema) => schema.notRequired(),
      }),
      firstName: Yup.string()
        .min(2, `${t('minNameLetterLenght')}`)
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        .matches(englishLetterRegex, `${t('onlyEnglishLetters')}`)
        .when([], {
          is: () => checkout.user.clientType === 'individual',
          then: (schema) => schema.required(`${t('required')}`),
          otherwise: (schema) => schema.notRequired(),
        }),
      lastName: Yup.string()
        .min(2, `${t('minSurnameLetterLenght')}`)
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        .matches(englishLetterRegex, `${t('onlyEnglishLetters')}`)
        .when([], {
          is: () => checkout.user.clientType === 'individual',
          then: (schema) => schema.required(`${t('required')}`),
          otherwise: (schema) => schema.notRequired(),
        }),
      firstNameGe: Yup.string()
        .min(2, `${t('minNameLetterLenght')}`)
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        .matches(georgianLetterRegex, `${t('onlyGeorgianLetters')}`)
        .when('cityzenship', {
          is: (val: any) => {
            const isoCode = getCityzenshipIso(val)
            return isoCode === 'GE'
          },
          then: Yup.string().required(`${t('required')}`),
          otherwise: Yup.string(),
        }),
      lastNameGe: Yup.string()
        .min(2, `${t('minSurnameLetterLenght')}`)
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        .matches(georgianLetterRegex, `${t('onlyGeorgianLetters')}`)
        .when('cityzenship', {
          is: (val: any) => {
            const isoCode = getCityzenshipIso(val)
            return isoCode === 'GE'
          },
          then: Yup.string().required(`${t('required')}`),
          otherwise: Yup.string(),
        }),
      gender: Yup.string()
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        .when([], {
          is: () => checkout.user.clientType === 'individual',
          then: (schema) => schema.required(`${t('required')}`),
          otherwise: (schema) => schema.notRequired(),
        }),
      clientEmail: Yup.string()
        .trim()
        .matches(/^\S*$/, t('spaceValidation'))
        .test('only-english-chars', t('enterValidMail'), (value) => {
          if (!value) return true
          // Only allow English letters, numbers, @ and . (for email format)
          return /^[a-zA-Z0-9@.]+$/.test(value)
        })
        .email(t('enterValidMail'))
        .required(`${t('required')}`),
      clientPhone: Yup.string()
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        .required(`${t('required')}`),
      clientDocTypeId: Yup.number()
        .typeError(`${t('required')}`)
        .required(`${t('required')}`),
      consentTerm: Yup.boolean().oneOf([true]).required(),
      isOwner: Yup.boolean().notRequired(),

      registrationCountry: Yup.string().when([], {
        is: () => checkout.user.clientType === 'individual',
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(t('required')),
      }),
      identificationNumber: Yup.string().when([], {
        is: () => checkout.user.clientType === 'individual',
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(t('required')),
      }),
      legalEntityType: Yup.string().when([], {
        is: () => checkout.user.clientType === 'individual',
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required(t('required')),
      }),
      companyName: Yup.string().when([], {
        is: () => checkout.user.clientType === 'individual',
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema.matches(/^[A-Za-z0-9][A-Za-z0-9 .,"'&()/-]*$/, `${t('onlyEnglishLetters')}`).required(t('required')),
      }),
      representativeName: Yup.string().when([], {
        is: () => checkout.user.clientType === 'individual',
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.matches(englishLetterRegex, `${t('onlyEnglishLetters')}`).required(t('required')),
      }),
      representativeSurname: Yup.string().when([], {
        is: () => checkout.user.clientType === 'individual',
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.matches(englishLetterRegex, `${t('onlyEnglishLetters')}`).required(t('required')),
      }),
      clientDocOneSide: Yup.string().test('clientDocOneSide-required', t('required'), function (value) {
        const { clientDocTypeId } = this.parent // value from formik
        const n = client?.clientDocumentTypes?.find((d) => d.id === clientDocTypeId)?.neededSides

        if (n === undefined) return true
        if (n >= 1) return !!value
        return true
      }),

      clientDocSecondSide: Yup.string().test('clientDocSecondSide-required', t('required'), function (value) {
        const { clientDocTypeId } = this.parent
        const n = client?.clientDocumentTypes?.find((d) => d.id === clientDocTypeId)?.neededSides

        if (n === undefined) return true
        if (n >= 2) return !!value
        return true
      }),

      clientDocThirdSide: Yup.string().test('clientDocThirdSide-required', t('required'), function (value) {
        const { clientDocTypeId } = this.parent
        const n = client?.clientDocumentTypes?.find((d) => d.id === clientDocTypeId)?.neededSides

        if (n === undefined) return true
        if (n >= 3) return !!value
        return true
      }),
    })
  }, [checkout.user, t, getCityzenshipIso, client?.clientDocumentTypes])

  const initialValues = {
    cityzenship: checkout.user.cityzenship || '',
    idNumber: checkout.user.idNumber || '',
    socialSecurityNumber: checkout.user.socialSecurityNumber || '',
    dateOfbirth: checkout.user.dateOfbirth || '',
    firstName: checkout.user.translations?.en?.firstName || '',
    lastName: checkout.user.translations?.en?.lastName || '',
    firstNameGe: checkout.user.translations?.ge?.firstName || '',
    lastNameGe: checkout.user.translations?.ge?.lastName || '',
    gender: checkout.user.gender || '',
    clientEmail: checkout.user.clientEmail || '',
    clientPhone: checkout.user.clientPhone || '',
    clientDocTypeId: checkout.user.clientDocTypeId || undefined,
    clientDocOneSide: checkout.user.clientDocOneSide || undefined,
    clientDocSecondSide: checkout.user.clientDocSecondSide || undefined,
    clientDocThirdSide: checkout.user.clientDocThirdSide || undefined,
    consentTerm: checkout.user.consentTerm || null,
    isOwner: checkout.user.isOwner ?? false,
    registrationCountry: checkout.user.registrationCountry || '',
    identificationNumber: checkout.user.identificationNumber || '',
    legalEntityType: checkout.user.legalEntityType || '',
    companyName: checkout.user.companyName || '',
    representativeName: checkout.user.representativeName || '',
    representativeSurname: checkout.user.representativeSurname || '',
  }

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: ValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  })

  const { setFieldValue, setFieldTouched } = formik

  const handleUserFieldChange = useCallback(
    (field: keyof InternationalTplUser, formField?: string) => (value: any) => {
      dispatch(
        setUserField({
          fieldName: field,
          fieldValue: value,
        }),
      )
      if (formField) {
        setFieldValue(formField, value, true)
        if (formField === 'clientEmail') {
          setFieldTouched('clientEmail', true, false)
        }
      }
    },
    [dispatch, setFieldValue, setFieldTouched],
  )

  const handleTranslationChange = useCallback(
    (language: 'ge' | 'en', translationField: 'firstName' | 'lastName', formField: string) => (value: string) => {
      dispatch(
        setUserTranslationField({
          language,
          fieldName: translationField,
          fieldValue: value,
        }),
      )
      setFieldValue(formField, value)
    },
    [dispatch, setFieldValue],
  )

  const handleDropdownSelect = useCallback(
    (field: keyof InternationalTplUser, formField?: string) => (option: { value: any }) => {
      const selectedValue = option?.value
      dispatch(
        setUserField({
          fieldName: field,
          fieldValue: selectedValue,
        }),
      )
      if (formField) {
        setFieldValue(formField, selectedValue)
      }
    },
    [dispatch, setFieldValue],
  )

  const handleClientPhoneChange = useCallback(
    (value: string, event: { dialCode: string }) => {
      handleUserFieldChange('clientPhone', 'clientPhone')(value)
      if (event?.dialCode) {
        dispatch(
          setUserField({
            fieldName: 'clientPhoneCountryCode',
            fieldValue: event.dialCode,
          }),
        )
      }
    },
    [dispatch, handleUserFieldChange],
  )

  // Helper function to calculate age from date string
  const calculateAgeFromDate = useCallback((dateString: any) => {
    if (typeof dateString === 'string') {
      const sanitized = dateString.replace(/\s/g, '')
      const parts = sanitized.split('/')
      if (parts.length !== 3) return null

      const birthDate = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]))
      const ageDiff = Date.now() - birthDate.getTime()
      const ageDate = new Date(ageDiff)

      return Math.abs(ageDate.getUTCFullYear() - 1970)
    }
    return null
  }, [])

  const closeTermsModal = () => {
    setTermsModalIsOpen(false)
  }

  // Search should match any translated country name (en/ge/hy/ru) and nicename.
  const getSearchableCountryNames = (item?: InternationalTplCountry | null): string[] => {
    if (!item) return []
    const names = [
      item.translations?.en?.name,
      item.translations?.ge?.name,
      item.translations?.hy?.name,
      item.translations?.ru?.name,
      item.nicename,
    ]
    return names.filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
  }

  const formatDateForInput = (value: string | undefined | null) => {
    if (!value) return ''

    const d = dayjs(value, ['YYYY-MM-DD', 'DD/MM/YYYY', 'D/M/YYYY', 'DD / MM / YYYY'], true)

    return d.isValid() ? d.format('DD / MM / YYYY') : value
  }

  const countrySearchFilter = useCallback((option: any, inputValue: string) => {
    const query = inputValue.trim().toLowerCase()
    if (!query) return true
    const data = option?.data ?? option
    const names = (data?.searchKeywords as string[] | undefined) ?? []
    return names.some((name) => name.toLowerCase().includes(query))
  }, [])

  const clearDocumentFields = useCallback(() => {
    dispatch(
      setUserField({
        fieldName: 'clientDocTypeId',
        fieldValue: undefined,
      }),
    )
    dispatch(
      setUserField({
        fieldName: 'clientDocOneSide',
        fieldValue: undefined,
      }),
    )
    dispatch(
      setUserField({
        fieldName: 'clientDocSecondSide',
        fieldValue: undefined,
      }),
    )
    dispatch(
      setUserField({
        fieldName: 'clientDocThirdSide',
        fieldValue: undefined,
      }),
    )
    setFieldValue('clientDocTypeId', undefined)
    setFieldValue('clientDocOneSide', undefined)
    setFieldValue('clientDocSecondSide', undefined)
    setFieldValue('clientDocThirdSide', undefined)
  }, [dispatch, setFieldValue])

  const diffage = calculateAgeFromDate(checkout.user?.dateOfbirth)

  const clientTypeCallback = useCallback(() => {
    if (client && client.clientTypes && client.clientTypes.length === 1) {
      let _client = client.clientTypes?.[0]
      //
      dispatch(
        setUserField({
          fieldName: 'clientType',
          fieldValue: _client.slug,
        }),
      )

      dispatch(
        setUserField({
          fieldName: 'clientTypeId',
          fieldValue: _client.id,
        }),
      )
    }
  }, [client, dispatch])

  useEffect(() => {
    clientTypeCallback()

    return () => {}
  }, [clientTypeCallback])

  // Set default clientType to 'individual' for non-AM countries
  useEffect(() => {
    if (filter.isoCode && !checkout.user.clientType) {
      const individualClient = client?.clientTypes?.find((x) => x.slug === 'individual')
      if (individualClient) {
        dispatch(
          setUserField({
            fieldName: 'clientType',
            fieldValue: individualClient.slug,
          }),
        )
        dispatch(
          setUserField({
            fieldName: 'clientTypeId',
            fieldValue: individualClient.id,
          }),
        )
      }
    }
  }, [filter.isoCode, checkout.user.clientType, client, dispatch])

  useEffect(() => {
    if (!auth?.user) return

    const {
      personalNumber,
      birthday,
      citizenship,
      email,
      phone,
      gender,
      firstNameGe,
      lastNameGe,
      firstName,
      lastName,
    } = auth.user

    const apply = <T,>(value: T | null | undefined, updater: (val: T) => void) => {
      if (value !== null && value !== undefined && value !== '') {
        updater(value)
      }
    }

    apply(personalNumber, handleUserFieldChange('idNumber', 'idNumber'))
    apply(birthday, handleUserFieldChange('dateOfbirth', 'dateOfbirth'))
    apply(citizenship, handleUserFieldChange('cityzenship', 'cityzenship'))
    apply(email, handleUserFieldChange('clientEmail', 'clientEmail'))
    apply(phone, handleUserFieldChange('clientPhone', 'clientPhone'))
    apply(gender, handleUserFieldChange('gender', 'gender'))
    apply(firstNameGe, handleTranslationChange('ge', 'firstName', 'firstNameGe'))
    apply(lastNameGe, handleTranslationChange('ge', 'lastName', 'lastNameGe'))
    apply(firstName, handleTranslationChange('en', 'firstName', 'firstName'))
    apply(lastName, handleTranslationChange('en', 'lastName', 'lastName'))
  }, [auth?.user, handleTranslationChange, handleUserFieldChange])

  // auto redirect first page
  useEffect(() => {
    if (!filter?.isoCode) {
      navigate('/international-tpl-country')
    }
    return () => {}
  }, [filter?.isoCode, navigate])

  const MainLoader = isClientLoading || isPageInfoLoading || isPagesLoading

  return MainLoader ? (
    <Loader />
  ) : (
    <Wrapper>
      <PageHeader total={totalCount} passed={passedCount} pages={displayPages} />
      <DialogModal
        title={t('consent')}
        isOpen={termsModalIsOpen}
        onClose={closeTermsModal}
        src={`${document.location.origin}/consent-dialog`}
        acceptOnClick={() => [
          dispatch(
            setUserField({
              fieldName: 'consentTerm',
              fieldValue: true,
            }),
          ),
          formik.setFieldValue('consentTerm', true),
          closeTermsModal(),
        ]}
        //
        declineOnClick={() => [
          dispatch(
            setUserField({
              fieldName: 'consentTerm',
              fieldValue: false,
            }),
          ),
          formik.setFieldValue('consentTerm', false),
          closeTermsModal(),
        ]}
      />
      <Container>
        <Content>
          <Title text={pageInfo?.translations?.[lang]?.title} />
          <Block>
            {filter.isoCode === 'AM' ? (
              <ButtonBlock>
                {client?.clientTypes?.map((item, index) => (
                  <button
                    key={index}
                    className={checkout?.user?.clientType?.toLowerCase() === item?.slug?.toLowerCase() ? 'active' : ''}
                    onClick={() => {
                      dispatch(
                        setUserField({
                          fieldName: 'clientType',
                          fieldValue: item.slug,
                        }),
                      )

                      dispatch(
                        setUserField({
                          fieldName: 'clientTypeId',
                          fieldValue: item.id,
                        }),
                      )

                      clearDocumentFields()
                    }}
                  >
                    {item.translations?.[lang]?.name}
                  </button>
                ))}
              </ButtonBlock>
            ) : null}

            {filter.isoCode === 'AM' && !checkout.user.clientType ? null : (
              <>
                {checkout.user.clientType === 'individual' || !checkout.user.clientType ? (
                  <>
                    {internationalTplCountries && (
                      <Dropdown
                        style={{ width: '100%' }}
                        isLoading={isInternationalTplCountriesLoading}
                        title={`${t('cityzenship')}`}
                        isSearchable={true}
                        value={
                          checkout.user?.countryId
                            ? internationalTplCountries.find((x) => x.id === checkout.user?.countryId)?.iso
                            : checkout.user?.cityzenship
                        }
                        isDisabled={personalFieldsDisabled}
                        options={[
                          ...internationalTplCountries.map((c) => ({
                            value: c.iso,
                            label: c.translations?.[lang]?.name,
                            searchKeywords: getSearchableCountryNames(c),
                          })),
                        ]}
                        filterOption={countrySearchFilter}
                        onChange={(option: { value: any }) => {
                          const selectedCountry = internationalTplCountries?.find((c) => c.iso === option?.value)
                          if (selectedCountry) {
                            dispatch(
                              setUserField({
                                fieldName: 'countryId',
                                fieldValue: selectedCountry.id,
                              }),
                            )
                            dispatch(
                              setUserField({
                                fieldName: 'cityzenship',
                                fieldValue: selectedCountry.iso,
                              }),
                            )
                            setFieldValue('cityzenship', selectedCountry.iso)
                          }
                        }}
                        error={!!(formik.errors.cityzenship && checkout.user.cityzenship)}
                        onBlur={formik.handleBlur}
                      />
                    )}

                    {getCityzenshipIso(checkout.user?.cityzenship)?.toLowerCase() === 'hy' &&
                      checkout.user?.clientType === 'individual' && (
                        <Input
                          id="social-security-number"
                          name="socialSecurityNumber"
                          label={`${t('socialSecurityNumber')}`}
                          type="text"
                          placeholder=""
                          value={checkout.user.socialSecurityNumber || ''}
                          onChange={handleUserFieldChange('socialSecurityNumber', 'socialSecurityNumber')}
                          width={'fullWidth'}
                          error={!!(formik.errors.socialSecurityNumber && checkout.user.socialSecurityNumber)}
                          errorText={formik.errors.socialSecurityNumber}
                          disabled={personalFieldsDisabled}
                          onBlur={formik.handleBlur}
                        />
                      )}

                    <Input
                      id="id-number"
                      name="idNumber"
                      label={`${t('personalNumber')}`}
                      type="text"
                      placeholder=""
                      value={checkout.user.idNumber || ''}
                      onChange={handleUserFieldChange('idNumber', 'idNumber')}
                      width={'fullWidth'}
                      error={!!(formik.errors.idNumber && checkout.user.idNumber)}
                      errorText={formik.errors.idNumber}
                      disabled={personalFieldsDisabled}
                      onBlur={formik.handleBlur}
                    />

                    {checkout.user.dateOfbirth !== undefined && diffage && diffage < 18 ? (
                      <WarningMessage variant="light-red" text={t('ageIsNotValid')} />
                    ) : null}

                    <BirthDayInput
                      width={'fullWidth'}
                      id="date-of-birth"
                      label={t('dateOfBirth')}
                      placeholder={t('dayMonthYear')}
                      value={formatDateForInput((checkout.user.dateOfbirth as any) || '')}
                      disabled={personalFieldsDisabled}
                      onChange={handleUserFieldChange('dateOfbirth', 'dateOfbirth')}
                    />

                    {checkout.user.cityzenship === 'GE' ? (
                      <>
                        <Input
                          id="georgian-first-name"
                          name="firstNameGe"
                          label={`${t('nameInGeorgia')}`}
                          type="text"
                          placeholder=""
                          value={checkout.user.translations?.ge?.firstName || ''}
                          onChange={handleTranslationChange('ge', 'firstName', 'firstNameGe')}
                          width={'fullWidth'}
                          error={!!(formik.errors.firstNameGe && checkout.user.translations?.ge?.firstName)}
                          errorText={formik.errors.firstNameGe}
                          disabled={personalFieldsDisabled}
                          onBlur={formik.handleBlur}
                        />

                        <Input
                          id="georgian-last-name"
                          name="lastNameGe"
                          label={`${t('surnameInGeorgia')}`}
                          type="text"
                          placeholder=""
                          value={checkout.user.translations?.ge?.lastName || ''}
                          onChange={handleTranslationChange('ge', 'lastName', 'lastNameGe')}
                          width={'fullWidth'}
                          error={!!(formik.errors.lastNameGe && checkout.user.translations?.ge?.lastName)}
                          errorText={formik.errors.lastNameGe}
                          disabled={personalFieldsDisabled}
                          onBlur={formik.handleBlur}
                        />
                      </>
                    ) : null}

                    <Input
                      id="english-first-name"
                      name="firstNameEn"
                      label={`${t('nameInEnglish')}`}
                      type="text"
                      placeholder=""
                      value={checkout.user.translations?.en?.firstName || ''}
                      onChange={handleTranslationChange('en', 'firstName', 'firstName')}
                      width={'fullWidth'}
                      error={!!(formik.errors.firstName && checkout.user.translations?.en?.firstName)}
                      errorText={formik.errors.firstName}
                      disabled={personalFieldsDisabled}
                      onBlur={formik.handleBlur}
                    />

                    <Input
                      id="english-last-name"
                      name="lastNameEn"
                      label={`${t('surnameInEnglish')}`}
                      type="text"
                      placeholder=""
                      value={checkout.user.translations?.en?.lastName || ''}
                      onChange={handleTranslationChange('en', 'lastName', 'lastName')}
                      width={'fullWidth'}
                      error={!!(formik.errors.lastName && checkout.user.translations?.en?.lastName)}
                      errorText={formik.errors.lastName}
                      disabled={personalFieldsDisabled}
                      onBlur={formik.handleBlur}
                    />

                    <GenderBlock>
                      {genderOptions.map((item, index) => (
                        <GenderCard
                          key={index}
                          pet={false}
                          selected={checkout?.user?.gender === item}
                          gender={item}
                          disabled={personalFieldsDisabled}
                          femaleText={`${t('female')}`}
                          maleText={`${t('male')}`}
                          onClick={(value) => handleUserFieldChange('gender', 'gender')(value)}
                          error={!!(formik.errors.gender && checkout.user.gender)}
                          errorText={formik.errors.gender}
                          onBlur={formik.handleBlur}
                        />
                      ))}
                    </GenderBlock>
                  </>
                ) : null}

                {/* ################################################################## */}

                {filter.isoCode === 'AM' && checkout.user.clientType === 'legal' ? (
                  <>
                    {internationalTplCountries && (
                      <Dropdown
                        style={{ width: '100%' }}
                        isLoading={isInternationalTplCountriesLoading}
                        title={`${t('countryOfRegistration')}`}
                        isSearchable={true}
                        value={
                          checkout.user?.registrationCountryId
                            ? internationalTplCountries.find((x) => x.id === checkout.user?.registrationCountryId)?.iso
                            : checkout.user?.registrationCountry
                        }
                        options={[
                          ...internationalTplCountries.map((c) => ({
                            value: c.iso,
                            label: c.translations?.[lang]?.name,
                          })),
                        ]}
                        onChange={(option: { value: any }) => {
                          const selectedCountry = internationalTplCountries?.find((c) => c.iso === option?.value)
                          if (selectedCountry) {
                            dispatch(
                              setUserField({
                                fieldName: 'registrationCountryId',
                                fieldValue: selectedCountry.id,
                              }),
                            )
                            dispatch(
                              setUserField({
                                fieldName: 'registrationCountry',
                                fieldValue: selectedCountry.iso,
                              }),
                            )
                            setFieldValue('registrationCountry', selectedCountry.iso)
                          }
                        }}
                        error={!!(formik.errors.registrationCountry && checkout.user.registrationCountry)}
                        onBlur={formik.handleBlur}
                      />
                    )}

                    <Input
                      id="identificationNumber"
                      name="identificationNumber"
                      label={`${t('identificationNumber')}`}
                      type="text"
                      placeholder=""
                      value={checkout.user.identificationNumber || ''}
                      onChange={handleUserFieldChange('identificationNumber', 'identificationNumber')}
                      width={'fullWidth'}
                      error={!!(formik.errors.identificationNumber && checkout.user.identificationNumber)}
                      errorText={formik.errors.identificationNumber}
                      onBlur={formik.handleBlur}
                    />

                    {client && client.clientSubtypes && (
                      <Dropdown
                        style={{ width: '100%' }}
                        isLoading={isClientLoading}
                        title={`${t('legalEntityType')}`}
                        isSearchable={true}
                        value={
                          client.clientSubtypes.find((x) => x.ligaApiId === checkout.user?.legalEntityType)
                            ?.ligaApiId || undefined
                        }
                        options={[
                          ...client.clientSubtypes.map((c) => ({
                            value: c.ligaApiId,
                            label: c.translations?.[lang]?.name,
                          })),
                        ]}
                        onChange={handleDropdownSelect('legalEntityType', 'legalEntityType')}
                        error={!!(formik.errors.legalEntityType && checkout.user.legalEntityType)}
                        onBlur={formik.handleBlur}
                      />
                    )}

                    <Input
                      id="companyName"
                      name="companyName"
                      label={`${t('_companyName')}`}
                      type="text"
                      placeholder=""
                      value={checkout.user.companyName || ''}
                      onChange={handleUserFieldChange('companyName', 'companyName')}
                      width={'fullWidth'}
                      error={!!(formik.errors.companyName && checkout.user.companyName)}
                      errorText={formik.errors.companyName}
                      onBlur={formik.handleBlur}
                    />

                    <Input
                      id="representativeName"
                      name="representativeName"
                      label={`${t('representativeName')}`}
                      type="text"
                      placeholder=""
                      value={checkout.user.representativeName || ''}
                      onChange={handleUserFieldChange('representativeName', 'representativeName')}
                      width={'fullWidth'}
                      error={!!(formik.errors.representativeName && checkout.user.representativeName)}
                      errorText={formik.errors.representativeName}
                      onBlur={formik.handleBlur}
                    />

                    <Input
                      id="representativeSurname"
                      name="representativeSurname"
                      label={`${t('representativeSurname')}`}
                      type="text"
                      placeholder=""
                      value={checkout.user.representativeSurname || ''}
                      onChange={handleUserFieldChange('representativeSurname', 'representativeSurname')}
                      width={'fullWidth'}
                      error={!!(formik.errors.representativeSurname && checkout.user.representativeSurname)}
                      errorText={formik.errors.representativeSurname}
                      onBlur={formik.handleBlur}
                    />
                  </>
                ) : null}

                <hr style={{ width: '100%', margin: 0 }} />
                <div>
                  <p style={{ textAlign: 'left' }}>{t('contactInfo')}</p>

                  <Input
                    id="client-email"
                    name="clientEmail"
                    label={`${t('email')}`}
                    type="email"
                    placeholder=""
                    value={checkout.user.clientEmail || ''}
                    onChange={handleUserFieldChange('clientEmail', 'clientEmail')}
                    width={'fullWidth'}
                    error={!!(formik.errors.clientEmail && checkout.user.clientEmail)}
                    errorText={formik.errors.clientEmail}
                    disabled={personalFieldsDisabled}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <PhoneNumberInput
                  id="client-phone-number"
                  hideButtons={true}
                  label={`${t('phone')}`}
                  value={checkout.user.clientPhone || ''}
                  onChange={handleClientPhoneChange}
                  error={!!(formik.errors.clientPhone && checkout.user.clientPhone)}
                  errorText={formik.errors.clientPhone}
                  onBlur={formik.handleBlur}
                />

                {client && client.clientDocumentTypes ? (
                  <Dropdown
                    key={`document-type-${checkout.user?.clientType || 'none'}-${
                      checkout.user?.clientDocTypeId || 'empty'
                    }`}
                    style={{ width: '100%' }}
                    title={`${t('documentType')}`}
                    isSearchable={false}
                    isLoading={isClientLoading}
                    value={(() => {
                      // Get available options for current client type
                      const availableOptions = (() => {
                        if (filter.isoCode === 'TR') {
                          return (
                            client.clientDocumentTypes?.map((item) => ({
                              value: item.id,
                              label: item.translations?.[lang]?.name,
                            })) || []
                          )
                        }

                        const currentClientTypeId = client.clientTypes?.find(
                          (x) => x.slug === checkout?.user?.clientType,
                        )?.id

                        return (
                          client.clientDocumentTypes
                            ?.filter((x) => x.clientTypeId === currentClientTypeId)
                            ?.map((item) => ({
                              value: item.id,
                              label: item.translations?.[lang]?.name,
                            })) || []
                        )
                      })()

                      // Only return value if it exists in available options
                      const currentValue = checkout.user?.clientDocTypeId
                      if (!currentValue) return undefined
                      const isValidValue = availableOptions.some((opt) => opt.value === currentValue)
                      return isValidValue ? currentValue : undefined
                    })()}
                    options={(() => {
                      if (filter.isoCode === 'TR') {
                        return (
                          client.clientDocumentTypes?.map((item) => ({
                            value: item.id,
                            label: item.translations?.[lang]?.name,
                          })) || []
                        )
                      }

                      const currentClientTypeId = client.clientTypes?.find(
                        (x) => x.slug === checkout?.user?.clientType,
                      )?.id

                      return (
                        client.clientDocumentTypes
                          ?.filter((x) => x.clientTypeId === currentClientTypeId)
                          ?.map((item) => ({
                            value: item.id,
                            label: item.translations?.[lang]?.name,
                          })) || []
                      )
                    })()}
                    onChange={handleDropdownSelect('clientDocTypeId', 'clientDocTypeId')}
                    error={!!(formik.errors.clientDocTypeId && checkout.user.clientDocTypeId)}
                    onBlur={formik.handleBlur}
                  />
                ) : null}

                {client &&
                client.clientDocumentTypes &&
                (client.clientDocumentTypes.find((x) => x.id === checkout.user.clientDocTypeId)
                  ?.neededSides as number) >= 1 ? (
                  <UploadFile
                    label={t('document')}
                    onChange={(value) => [
                      dispatch(
                        setUserField({
                          fieldName: 'clientDocOneSide',
                          fieldValue: value,
                        }),
                      ),
                      formik.setFieldValue('clientDocOneSide', value),
                    ]}
                    defaultFileName={checkout.user.clientDocOneSide ?? ''}
                  />
                ) : null}

                {client &&
                client.clientDocumentTypes &&
                (client.clientDocumentTypes.find((x) => x.id === checkout.user.clientDocTypeId)
                  ?.neededSides as number) >= 2 ? (
                  <UploadFile
                    label={t('document')}
                    onChange={(value) => [
                      dispatch(
                        setUserField({
                          fieldName: 'clientDocSecondSide',
                          fieldValue: value,
                        }),
                      ),
                      formik.setFieldValue('clientDocSecondSide', value),
                    ]}
                    defaultFileName={checkout.user.clientDocSecondSide ?? ''}
                  />
                ) : null}

                {client &&
                client.clientDocumentTypes &&
                (client.clientDocumentTypes.find((x) => x.id === checkout.user.clientDocTypeId)
                  ?.neededSides as number) === 3 ? (
                  <UploadFile
                    label={t('document')}
                    onChange={(value) => [
                      dispatch(
                        setUserField({
                          fieldName: 'clientDocThirdSide',
                          fieldValue: value,
                        }),
                      ),
                      formik.setFieldValue('clientDocThirdSide', value),
                    ]}
                    defaultFileName={checkout.user.clientDocThirdSide ?? ''}
                  />
                ) : null}

                {checkout.user.clientType === 'individual' ? (
                  <Checkbox
                    id="international-tpl-owner"
                    label={`${t('iAmOwner')}`}
                    checked={checkout.user.isOwner || false}
                    defaultChecked={checkout.user.isOwner || false}
                    onChange={(value) => [
                      dispatch(
                        setUserField({
                          fieldName: 'isOwner',
                          fieldValue: value,
                        }),
                      ),
                      formik.setFieldValue('isOwner', value),
                    ]}
                  />
                ) : null}

                <Checkbox
                  id="pet-user-frani-terms"
                  label={`${t('franiTermsText')}`}
                  defaultChecked={checkout.user.consentTerm || false}
                  error={!!(formik.errors.consentTerm && checkout.user.consentTerm === false)}
                  onClick={() => setTermsModalIsOpen(true)}
                />
              </>
            )}
          </Block>

          <Buttons>
            <NavigationButton
              text={`${t('back')}`}
              buttonType="prev"
              onClick={() => navigate('/international-tpl-days')}
            />
            <NavigationButton
              text={`${t('next')}`}
              buttonType="next"
              disabled={!formik.isValid}
              onClick={() => {
                const selectedCountry = internationalTplCountries?.find((c) => c.iso === checkout.user.cityzenship)
                if (filter.isoCode === 'TR') {
                  dispatch(
                    setUserField({
                      fieldName: 'clientType',
                      fieldValue: 'individual',
                    }),
                  )
                }
                if (checkout.user.cityzenship) {
                  dispatch(
                    setUserField({
                      fieldName: 'countryId',
                      fieldValue: selectedCountry?.id,
                    }),
                  )
                }
                navigate('/international-tpl-car-info')
              }}
            />
          </Buttons>
        </Content>
      </Container>
    </Wrapper>
  )
}

const ButtonBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  width: 100%;

  button {
    background-color: ${Colors.grey};
    color: ${Colors.Black};
    outline: none;
    border-radius: 20px;
    width: 155px;
    text-transform: capitalize;
    border: none;
    padding-block: 7px;
    font-size: 14px;
  }

  button.active {
    background-color: ${Colors.Black};
    color: ${Colors.white};
  }
`

const GenderBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;
  width: 100%;
`

const Content = styled.div`
  margin-top: 40px;
  margin-bottom: 32px;
  z-index: 2;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;

  ${Responsive.mobile} {
    margin-top: 24px;
  }
`
const Block = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  max-width: 432px;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  background-color: ${Colors.white};
  border-radius: 8px;
  padding: 20px 32px;
`
const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  min-height: 100vh;
`

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  width: 100%;
  margin-top: 48px;
  max-width: 368px;

  button {
    width: 100%;
    padding-inline: 0px;
    &:first-child {
      margin-right: 20px;
    }
  }

  ${Responsive.mobile} {
    max-width: 296px;
    margin-top: 40px;
    button {
      &:first-child {
        margin-right: 15px;
      }
    }
  }
`

export default InternationalTplPersonalInfo
